// react
import React, { useState, useRef, useEffect } from 'react';

type ScrollableTimeSelectorProps = {
	value: string;
	options: string[];
	onChange: (value: string) => void;
	disabled?: boolean;
	label?: string;
};

const ScrollableTimeSelector: React.FC<ScrollableTimeSelectorProps> = ({ value, options, onChange, disabled = false, label }) => {
	const [isOpen, setIsOpen] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);
	const selectedRef = useRef<HTMLButtonElement>(null);

	// Don't allow expansion if there are no options or only one option
	const canExpand = !disabled && options.length > 1;

	// Format value for display (add spaces around colon if it's a time format)
	const formatDisplayValue = (val: string): string => {
		if (val.includes(':')) {
			return val.replace(':', ' : ');
		}
		return val;
	};

	useEffect(() => {
		if (isOpen && selectedRef.current && scrollRef.current) {
			// Scroll to center the selected item (showing 2 before and 2 after)
			setTimeout(() => {
				if (selectedRef.current) {
					selectedRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
				}
			}, 10);
		}
	}, [isOpen, value]);

	const handleSelect = (option: string) => {
		onChange(option);
		setIsOpen(false);
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (scrollRef.current && !scrollRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	return (
		<div className="relative flex-1">
			<button
				type="button"
				onClick={() => canExpand && setIsOpen(!isOpen)}
				disabled={disabled || options.length === 0}
				className={`
					w-full
					py-3
					px-4
					rounded-[12px]
					border-none
					font-medium
					bg-white
					text-left
					flex
					items-center
					justify-between
					shadow-[0_2px_8px_rgba(0,0,0,0.12)]
					focus:outline-none
					${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
				`}
				style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)' }}
			>
				<span 
					className={value ? 'text-[#000000]' : 'text-gray-400'}
					style={{ fontSize: '17px' }}
				>
					{value ? formatDisplayValue(value) : label || 'Select time'}
				</span>
				<svg 
					width="12" 
					height="12" 
					viewBox="0 0 12 12" 
					fill="none"
					className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
				>
					<path d="M6 3L1 8h10L6 3z" fill="#4B5B4B"/>
				</svg>
			</button>
			{isOpen && canExpand && (
				<div 
					ref={scrollRef}
					className="w-full mt-1 bg-white rounded-[12px] overflow-hidden"
					style={{ height: '144px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
				>
					<div className="h-full overflow-y-auto">
						{options.map((option) => {
							const isSelected = option === value;
							return (
								<button
									key={option}
									ref={isSelected ? selectedRef : null}
									type="button"
									onClick={() => handleSelect(option)}
									className={`
										w-full
										py-3
										px-4
										text-center
										border-b border-gray-200 last:border-b-0
										border-x-0
										min-h-[48px]
										flex
										items-center
										justify-center
										${isSelected
											? 'bg-white text-[#000000] font-bold'
											: 'bg-white text-gray-400'
										}
									`}
									style={{ 
										fontSize: '17px',
										...(isSelected ? { boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)' } : {})
									}}
								>
									{formatDisplayValue(option)}
								</button>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};

export default ScrollableTimeSelector;

